/**
 * User Resource Hooks
 *
 * Type-safe hooks for user-related API operations.
 * Built on top of the generic API hooks with user-specific defaults.
 *
 * Note: Named with "Query" suffix to avoid conflicts with app-level useUser context hook.
 */

import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

import { queryKeys } from '../../queryKeys';
import type { QueryParamRecord } from '../../types';
import { useApiCreate, useApiDelete, useApiUpdate } from '../useApiMutation';
import { useApiGet, useApiList } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type UserListResponse = paths['/api/v1/users']['get']['responses']['200']['content']['application/json'];
type UserResponse = paths['/api/v1/users/{id}']['get']['responses']['200']['content']['application/json'];
type UserCreateBody = paths['/api/v1/users']['post']['requestBody']['content']['application/json'];
type UserUpdateBody = NonNullable<paths['/api/v1/users/{id}']['patch']['requestBody']>['content']['application/json'];

// Extract the user type from the response
type User = NonNullable<UserResponse['data']>;
type UserListFilters = paths['/api/v1/users']['get']['parameters']['query'];

// =============================================================================
// Query Hooks
// =============================================================================

interface UseUsersQueryOptions {
  enabled?: boolean;
  filters?: UserListFilters;
}

/**
 * Fetch a paginated list of users
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useUsersQuery({
 *   filters: { status: 'ACTIVE', limit: 20 }
 * });
 * ```
 */
export function useUsersQuery(options?: UseUsersQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/users', {
    enabled,
    queryKey: queryKeys[ResourceType.USERS].list(filters as QueryParamRecord),
    queryParams: filters,
  });
}

interface UseUserQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch a single user by ID
 *
 * @example
 * ```tsx
 * const { data: user, isLoading } = useUserQuery(userId);
 * ```
 */
export function useUserQuery(id: string, options?: UseUserQueryOptions) {
  return useApiGet(
    '/api/v1/users/{id}',
    { id },
    {
      enabled: options?.enabled ?? !!id,
      queryKey: queryKeys[ResourceType.USERS].detail(id),
    },
  );
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseCreateUserOptions {
  onError?: (error: Error) => void;
  onSuccess?: (user: UserResponse) => void;
}

/**
 * Create a new user
 *
 * @example
 * ```tsx
 * const createUser = useCreateUser({
 *   onSuccess: (response) => {
 *     toast.success(`User ${response.data?.email} created`);
 *   },
 * });
 *
 * createUser.mutate({ email: 'new@example.com', userType: 'CONTRACTOR' });
 * ```
 */
export function useCreateUser(options?: UseCreateUserOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users',
  });
}

interface UseUpdateUserOptions {
  onError?: (error: Error) => void;
  onSuccess?: (user: UserResponse) => void;
}

/**
 * Update an existing user
 *
 * @example
 * ```tsx
 * const updateUser = useUpdateUser();
 *
 * updateUser.mutate({
 *   pathParams: { id: userId },
 *   body: { status: 'ACTIVE' },
 * });
 * ```
 */
export function useUpdateUser(options?: UseUpdateUserOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users/{id}',
  });
}

interface UseDeleteUserOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Delete a user (soft delete)
 *
 * @example
 * ```tsx
 * const deleteUser = useDeleteUser({
 *   onSuccess: () => navigate('/users'),
 * });
 *
 * deleteUser.mutate({ id: userId });
 * ```
 */
export function useDeleteUser(options?: UseDeleteUserOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/users/{id}',
  });
}

// =============================================================================
// Exports
// =============================================================================

export type { User, UserCreateBody, UserListFilters, UserListResponse, UserResponse, UserUpdateBody };
